
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IGatsbyImageData } from 'gatsby-plugin-image';
import ReactPlayer from 'react-player';

import Image from '@klv/atoms/image';
import { generateClassName } from '@klv/shared/utils/functions';

import * as styles from './styles/socialCard.module.css';

import type { ISocialCard } from './types';

const SocialCard = ({ 
  backgroundColor = 'dark',
  swipe, 
  $current, 
  $index, 
  $activeIndex,
  slidePlaying,
  setSlidePlaying,
  setThisSlide,
  attributionCompany,
  attributionJobTitle,
  attributionName,
  videoPoster, 
  videoUrl, 
  videoDuration, 
  quote, 
  tag, 
  logo, 
  className = '',
}: ISocialCard) => {
  const { t } = useTranslation();

  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setHydrated(true);
  }, []);

  const clickHandler = () => {
    /*
    On a desktop, any video should be playable at any time.
    On mobile only the currently active video can be played
    */
    if ($current !== undefined || window.matchMedia('(min-width: 1024px)').matches) {
      setSlidePlaying($index);
      setThisSlide($index);
    } else {
      /*
      If we're on mobile and made it here, it means we clicked a video that is not on
      the current active card. In this case, trigger the correct swipe direction.
      */
      if ($index < $activeIndex) {
        swipe(-1);
      } else {
        swipe(1);
      }
      // On swipe, reset slidePlaying to none.
      setSlidePlaying(-1);
    }
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      event.code === 'Space'
      || event.code === 'Enter'
    ) {
      event.preventDefault();
      clickHandler();
    }
  };

  return (
    <div 
      className={generateClassName([
        styles.card,
        className,
        styles[backgroundColor],
      ])} 
      onClick={clickHandler}
      onKeyUp={handleKeyUp}
      onFocus={() => {setThisSlide($index);}}
      data-social-card
      tabIndex={0}
    >
      <div className={styles.videoContainer}> 
        <div className={generateClassName([styles.videoBlur, slidePlaying === $index ? styles.isPlaying : ''])}>
          {logo && <Image className={styles.logo} imageData={logo?.gatsbyImage as IGatsbyImageData} altText={logo?.altText}/>}
          <div className={styles.playButtonWrapper}>
            <div className={styles.playButtonCanvas}>
              <div className={styles.playButton}>
                <svg width="13" height="16" viewBox="0 0 13 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.5 7.13398C13.1667 7.51888 13.1667 8.48113 12.5 8.86603L2 14.9282C1.33333 15.3131 0.499999 14.832 0.499999 14.0622L0.5 1.93782C0.5 1.16802 1.33333 0.686896 2 1.0718L12.5 7.13398Z" fill="white"/>
                </svg>
              </div>
              {videoDuration && <span className={styles.playButtonTimer}>{videoDuration}</span>}
            </div>
          </div>
        </div>
        {hydrated && ( <ReactPlayer
          className={styles.videoPlayer}
          aria-label={videoPoster?.altText}
          config={{
            vimeo: {
              playerOptions: {
                controls: true,
                responsive: true,
                autopause: true,
                texttrack: t('langCodes_recaptchaLanguage') === 'en' ? '' : t('langCodes_recaptchaLanguage'),         
              },
            },
          }} 
          onEnded={() => {setSlidePlaying(-1);}}
          playing={slidePlaying === $index}
          url={videoUrl}
          height="100%"
          width="100%"
          style={{ display:slidePlaying === $index ? 'block' : 'none' }}
        />)}
        <Image className={generateClassName([styles.videoPoster, slidePlaying === $index ? styles.isPlaying : ''])} imageData={videoPoster?.gatsbyImage as IGatsbyImageData} altText={videoPoster?.altText}/>
      </div>
      <div className={styles.cardContents}>
      {quote && <div className={styles.quote}>"{quote}"</div>}
      {(attributionCompany || attributionJobTitle || attributionName ) &&  (
        <div className={styles.attributions}>
          {attributionCompany && <div>{attributionCompany}</div>}
          {attributionJobTitle && <div>{attributionJobTitle}</div>}
          {attributionName && <div>{attributionName}</div>}
        </div>
      )}
        {tag && (
          <div>
            <span className={styles.tag}>{tag}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialCard;
